"use client";
import React, {useContext} from "react";
import {MegaMenuContext} from "@/components/Layout/Header/Navigation/MainNavProvider";
import clsx from "clsx";
import NavFocusButton from "@/components/Layout/Header/Navigation/NavFocusButton";
import useWindowDimensions from "@/libs/contentful/helper/useWindowDimension";
import {useTranslations} from "next-intl";


type Props = {
    locale: {
        languageCode: string,
        countryCode: string,
        languageName: string
    },
    allLocales: {
        languageCode: string,
        countryCode: string,
        languageName: string
    }[],
    environment: string | undefined
};

export const LocaleSwitcherClient = ({locale, allLocales, environment}: Props) => {
    const {isMegaMenuOpen} = useContext(MegaMenuContext);
    const { width } = useWindowDimensions();
    const t = useTranslations('MainMenu');
    // renamed parameter to avoid shadowing and make intent clear
    const handleLanguageChange = (targetLocale: string) => {

        if(environment && environment == 'master'){
            switch (targetLocale) {
                case 'de-DE':
                    window.location.href = 'https://www.bonprix.de/corporate/';
                    break;
                case 'de-AT':
                    window.location.href = 'https://www.bonprix.at/corporate/';
                    break;
                default:
                    window.location.href = 'https://en.bonprix.de/corporate/';
            }
        } else{
            // Use full page reload to match production behavior
            // If we're on localhost use path-based routing, otherwise change the host subdomain
            const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

            if (isLocalhost) {
                switch (targetLocale) {
                    case 'de-DE':
                        window.location.href = '/corporate/de-DE/';
                        break;
                    case 'de-AT':
                        window.location.href = '/corporate/de-AT/';
                        break;
                    default:
                        window.location.href = '/corporate/';
                }
                return;
            }

            // For deployed non-master environments (stage, develop, etc.) replace the first hostname label with a locale-aware label
            const localeToHostLabel = (loc: string) => {
                switch (loc) {
                    case 'de-DE': return 'de';
                    case 'de-AT': return 'at';
                    default: return 'en';
                }
            }

            const host = window.location.hostname;
            const parts = host.split('.');
            const newFirstLabel = localeToHostLabel(targetLocale);

            if (parts.length > 1) {
                parts[0] = newFirstLabel;
                const newHost = parts.join('.');
                window.location.href = `${window.location.protocol}//${newHost}/corporate/`;
            } else {
                // fallback to stage domain routing
                switch (targetLocale) {
                    case 'de-DE':
                        window.location.href = '//de.dev-corporate-service.acns.bonprix.net/corporate/';
                        break;
                    case 'de-AT':
                        window.location.href = '//at.dev-corporate-service.acns.bonprix.net/corporate/';
                        break;
                    default:
                        window.location.href = '//en.dev-corporate-service.acns.bonprix.net/corporate/';
                }
            }
        }

    };
    return (
        <>
            <NavFocusButton className="bg-brand-light-blue md:bg-transparent" menuId='language-menu' menuName={'language'}>
                <button
                    type="button"
                    className={`group/focus focus:outline-0 md:px-xl md:py-m inline-flex justify-between md:p-[0] md:items-center md:justify-center text-black w-full${isMegaMenuOpen ? ' group open' : ''}`}
                    id="language-selector"
                    aria-haspopup="true"
                    aria-expanded={isMegaMenuOpen}
                    aria-label={t("langSwitcher")}
                    tabIndex={width !== undefined && width < 834 ? -1 : undefined}
                >
                    <span className="hidden group-focus/focus:outline-offset-[5px] group-focus/focus:outline-2 group-focus/focus:outline-outline group-focus/focus:outline md:block uppercase font-semibold border-b-[1px] border-white md:group-hover:border-b-[1px] md:group-hover:border-black">{locale.countryCode}</span>
                    <span className="block md:hidden text-link">{locale.languageName}</span>
                </button>
            </NavFocusButton>
            <div id="language-menu"
                className={clsx(!isMegaMenuOpen ? 'h-[0] invisible overflow-hidden md:opacity-0 md:pointer-events-none' : '', "w-full md:w-[200px] brand-transition md:absolute md:right-[0] md:pt-mobile md:shadow-lg bg-brand-light-blue md:bg-white")}
                role="menu"
                aria-orientation="vertical"
            >
                <div role="none">
                    {allLocales.map((singleLocale) => {
                        if (locale.languageCode === singleLocale.languageCode) {
                            return
                        }
                        return (
                            <button
                                key={singleLocale.languageCode}
                                onClick={() => handleLanguageChange(singleLocale.languageCode)}
                                className={`group/focus focus:outline-0 px-2xl py-m truncate text-link w-full text-left items-center block border-b border-brand-light-blue md:border-brand-light-cool-grey brand-transition font-normal md:font-semibold md:hover:bg-brand-hover md:focus:bg-brand-hover`}
                                role="menuitem"
                            >
                                <span className="group-focus/focus:outline-offset-[5px] group-focus/focus:outline-2 text-black group-focus/focus:outline-outline group-focus/focus:outline"> {singleLocale.languageName}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </>
    )
}